<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>{{ isEditMode ? "Edit Category" : "Add Category" }}</h1>
    </div>
    <div class="border-container-unique37">
      <form @submit.prevent="validateForm" novalidate>
        <div class="admin-form-group mb-3">
          <label>Title</label>
          <input type="text" v-model="form.title" required />
          <span v-if="errors.title" class="error-message-123">{{
            errors.title
          }}</span>
        </div>
        <div class="admin-form-group mb-3">
          <label>Name</label>
          <input type="text" v-model="form.name" required />
          <span v-if="errors.name" class="error-message-123">{{
            errors.name
          }}</span>
        </div>
        <div class="admin-form-group mb-3">
          <label>Module</label>
          <BaseInput
            type="multipleSelect"
            v-model="form.category_for"
            :options="categoryForOptions"
            :show-search="false"
            placeholder="Select categories"
            :maximum-multiple-select="-1"
          />
          <span v-if="errors.category_for" class="error-message-123">{{
            errors.category_for
          }}</span>
        </div>
        <BaseButton
          variant="primary"
          :loading="isLoading"
          :disabled="form.title === '' || form.name === ''"
          type="submit"
        >
          {{ isEditMode ? "Update" : "Submit" }}
        </BaseButton>
      </form>
    </div>
  </div>
</template>

<script setup>
import BaseInput from "@/components/BaseInput.vue";
import BaseButton from "@/components/common/BaseButton.vue";
import CategoryAdminService from "@/services/categoryAdminService";
import { CATEGORY_FOR_OPTIONS } from "@/utils/constants";
import { computed, inject, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const router = useRouter();
const route = useRoute();

const isLoading = ref(false);
const notify = inject("notify");
const form = reactive({
  title: "",
  name: "",
  category_for: [],
});

const errors = reactive({
  title: "",
  name: "",
  category_for: "",
});

const categoryForOptions = computed(() => CATEGORY_FOR_OPTIONS);

const isUpdatingSelection = ref(false);

watch(
  () => [...form.category_for],
  (newValues) => {
    if (isUpdatingSelection.value) return;

    isUpdatingSelection.value = true;

    const otherValues = categoryForOptions.value
      .filter((option) => option.value !== "all")
      .map((option) => option.value);

    if (
      newValues.includes("all") &&
      !isAllOthersSelected(newValues, otherValues)
    ) {
      form.category_for = ["all", ...otherValues];
    } else if (
      newValues.includes("all") &&
      newValues.length < otherValues.length + 1
    ) {
      form.category_for = newValues.filter((value) => value !== "all");
    }

    isUpdatingSelection.value = false;
  }
);

const isAllOthersSelected = (selectedValues, otherValues) => {
  return otherValues.every((value) => selectedValues.includes(value));
};

const isEditMode = route.params.id !== undefined;

const fetchCategory = async (id) => {
  try {
    const response = await CategoryAdminService.getById(id);
    if (response.status === 200) {
      const category = response.data.data;
      form.title = category.title;
      form.name = category.name;

      let categoryFor = category.category_for || [];

      const otherValues = categoryForOptions.value
        .filter((option) => option.value !== "all")
        .map((option) => option.value);

      if (otherValues.every((value) => categoryFor.includes(value))) {
        categoryFor = ["all", ...categoryFor];
      }

      form.category_for = categoryFor;
    }
  } catch (error) {
    console.error(error.message);
    alert("Failed to fetch category data");
  }
};

onMounted(() => {
  if (isEditMode) {
    fetchCategory(route.params.id);
  }
});

const validateForm = async () => {
  // Reset errors
  Object.keys(errors).forEach((key) => {
    errors[key] = "";
  });

  let isValid = true;

  // Validate required fields
  if (!form.title) {
    errors.title = "Title is required";
    isValid = false;
  }

  if (!form.name) {
    errors.name = "Name is required";
    isValid = false;
  }

  if (!isValid) {
    return;
  }

  // Remove "all" from the category_for array before sending to the API
  const apiFormData = {
    ...form,
    category_for: form.category_for.filter((item) => item !== "all"),
  };

  isLoading.value = true;
  try {
    if (isEditMode) {
      await CategoryAdminService.update(route.params.id, apiFormData);
      notify.success("Category updated successfully!", "Success");
    } else {
      await CategoryAdminService.create(apiFormData);
      notify.success("Category created successfully!", "Success");
    }
    router.push("/admin/categories");
  } catch (error) {
    console.error(error.message);
    if (error.response && error.response.data && error.response.data.message) {
      errors.name = error.response.data.message;
    } else {
      notify.error("Failed to submit form due to an unknown error!", "Error");
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
